import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import { Card, Typography, Divider, IconButton, Paper } from "@mhome/ui";
import LoadingSpinner from "../common/loading";
import { listPremades, searchPremades } from "../service/inspire-service";
import { useWebSocket } from "../ws/websocket";
import PremadeFilter from "./premade-filter";
import { SearchIcon } from "lucide-react";
import { useToast } from "../common/toast";
import PremadePopularityPreview from "./premade-popularity-preview";
import SharePopularityPreview from "./share-popularity-preview";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const PremadeGalleryView = ({ showFilter = false, setShowFilter }) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [premades, setPremades] = useState([]);
  const [devPremades, setDevPremades] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const prevSearchTextRef = useRef("");
  const { sendAndWaitWs } = useWebSocket();
  const [sortBy, setSortBy] = useState("popularity");
  const [filterText, setFilterText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const { successToast, errorToast } = useToast();
  const observerRef = useRef(null);

  useEffect(() => {
    setShowFilter(false);
    handleListPremades(true);
  }, []);

  //scrolling listener
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && !loading && hasMore) {
        handleListPremades(false);
      }
    }, options);

    observerRef.current = observer;

    const observerTarget = document.querySelector(".observer-target");
    if (observerTarget) {
      observer.observe(observerTarget);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [loading, hasMore]);

  const sortedPremades =
    premades
      ?.filter((p) =>
        p.summary.toLowerCase().includes(filterText.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === "alphabet") {
          const aName = a.summary.toLowerCase();
          const bName = b.summary.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName); //sort alphabetically
        }
      }) || [];

  const handleListPremades = async (isReset = false) => {
    if (loading || (!hasMore && !isReset)) return;
  };

  const handleAdopt = (data) => {
    history.push(
      `/automation/overview?premadeId=${data.id}&premadeVersion=${data.version}`
    );
  };

  const handleSearch = async () => {
    setLoading(true);
    const prevSearchText = prevSearchTextRef.current;
    if (searchText !== prevSearchText) {
      prevSearchTextRef.current = searchText;
      if (searchText.trim()) {
        setSearchMode(true);
        const resp = await searchPremades(sendAndWaitWs, searchText);
        if (resp !== null) {
          // Combine premadeEntries and sharedEntries
          const combinedPremades = [
            ...(resp.premadeEntries || []).map((entry) => ({
              ...entry,
              type: "premade",
            })),
            ...(resp.sharedEntries || []).map((entry) => ({
              ...entry,
              type: "shared",
            })),
          ];
          setPremades(combinedPremades);
          setDevPremades([]);
          setHasMore(false);
        }
      } else {
        setSearchMode(false);
        setPremades([]);
        setDevPremades([]);
        setHasMore(true);
        handleListPremades(true);
      }
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        height: "auto",
        overflowY: "auto",
      }}
    >
      {devPremades.length > 0 && (
        <div style={{ flexGrow: 1 }}>
          <DraftPremadesBlock premades={devPremades} onAdopt={handleAdopt} />
          <Divider />
        </div>
      )}
      <div style={{ padding: "8px 16px" }}>
        <Paper
          elevation={0}
          component="form"
          style={{
            padding: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            marginBottom: "8px",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <input
            type="text"
            style={{
              marginLeft: "8px",
              flex: 1,
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: "hsl(var(--foreground))",
              fontFamily: "inherit",
              fontSize: "1rem",
            }}
            placeholder="Search..."
            name="search"
            value={searchText}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchText(newValue);
              if (!newValue.trim()) {
                setSearchMode(false);
                setPremades([]);
                setDevPremades([]);
                setHasMore(true);
                handleListPremades(true);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <IconButton style={{ padding: "10px" }} onClick={handleSearch}>
            <SearchIcon />
          </IconButton>
        </Paper>
      </div>
      {loading && !premades.length ? (
        <LoadingSpinner />
      ) : (
        <>
          {showFilter && (
            <div
              style={{ flexGrow: 1, marginLeft: "16px", marginRight: "16px" }}
            >
              <PremadeFilter
                sortBy={sortBy}
                setSortBy={setSortBy}
                setFilterText={setFilterText}
                setShowFilter={setShowFilter}
              />
            </div>
          )}

          {premades.length === 0 ? (
            <div
              style={{ flexGrow: 1, marginLeft: "16px", marginRight: "16px" }}
            >
              <Typography
                variant="body1"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                Automation inspirations will be available soon.
              </Typography>
            </div>
          ) : (
            <>
              {!searchMode && (
                <>
                  <PremadePopularityPreview />
                  <SharePopularityPreview />
                </>
              )}
              <div
                style={{ flexGrow: 1, marginLeft: "16px", marginRight: "16px" }}
              >
                <PremadesList premades={sortedPremades} onAdopt={handleAdopt} />
              </div>

              <div
                className="observer-target"
                ref={(el) => {
                  if (el && observerRef.current) {
                    observerRef.current.observe(el);
                    // console.debug("Started observing element");
                  }
                }}
                style={{
                  height: "20px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {loading && <LoadingSpinner />}
              </div>

              {!loading && !hasMore && premades.length > 0 && (
                <Typography
                  variant="body2"
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "hsl(var(--muted-foreground))",
                  }}
                >
                  No more premades to load
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const DraftPremadesBlock = ({ premades, onAdopt }) => {
  const getComponentBgColor = useComponentBackgroundColor();

  return (
    <div
      style={{
        flexGrow: 1,
        padding: "16px",
        backgroundColor: getComponentBgColor("hsl(var(--accent))"),
      }}
    >
      <Typography variant="h4" style={{ marginBottom: "16px" }}>
        Dev Drafts
      </Typography>
      <PremadesList
        title={"Recommended"}
        premades={premades}
        onAdopt={onAdopt}
      />
    </div>
  );
};

export const PremadesList = ({ premades, onAdopt }) => {
  const getComponentBgColor = useComponentBackgroundColor();

  return (
    <>
      <ResponsiveCardGrid spacing={2} breakpoints={{ xs: 1, sm: 2, md: 3 }}>
        {premades.map((premade, index) => (
          <Card
            key={index}
            elevation={0}
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <div
              onClick={() => onAdopt(premade)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "8px",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                }}
              >
                <Typography
                  variant="body1"
                  component="div"
                  style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxHeight: "4.5em",
                    marginBottom: 0,
                  }}
                >
                  {premade.summary}
                </Typography>
              </div>
            </div>
          </Card>
        ))}
      </ResponsiveCardGrid>
    </>
  );
};

export default PremadeGalleryView;
